// src/components/layout/Topbar.jsx
import React, { useState, useEffect, useRef } from "react";
import comunicadosService from "../../services/comunicadosService";
import reservasService    from "../../services/reservasService";
import useSocket from "../../hooks/useSocket";
import { useAuth } from "../../contexts/AuthContext";
import MeuPerfilModal from "../MeuPerfilModal";
import ThemeToggle from "./ThemeToggle";
import { tocarBeepNotificacao } from "../../utils/pushNotifications";

const PERFIL_LABEL = { Admin:"Administrador", Gerente:"Gerente", Sindico:"Síndico", Condomino:"Condômino" };
const PERFIL_COLOR = { Admin:"#1B3A6B", Gerente:"#7C3AED", Sindico:"#166534", Condomino:"#C2410C" };

export default function Topbar({
  collapsed, onToggle, breadcrumbs=[], idCondominio,
  usuario=null, condominioAtivo=null, onTrocarCondominio=null, onLogout=null,
  onAbrirOnboarding=null, onNavigate=null,
  onBadgesChange=null,
}) {
  const { condominiosDisponiveis, logout } = useAuth();
  const [comunicados,  setComunicados]  = useState([]);
  const [reservasPend, setReservasPend] = useState([]);  // {id, nmArea, nmCondomino, nmUnidade, bloco, dtReserva, criadaEm}
  const [reservasCount,setReservasCount]= useState(0);
  const [showBell,     setShowBell]     = useState(false);
  const [showUser,     setShowUser]     = useState(false);
  const [showCondo,    setShowCondo]    = useState(false);
  const [perfilModal,  setPerfilModal]  = useState(null); // null | 'dados' | 'senha' | 'sessoes' | 'notif'
  const bellRef  = useRef(null);
  const userRef  = useRef(null);
  const condoRef = useRef(null);

  // Fonte única da verdade: lista vem do AuthContext (atualiza automaticamente
  // ao criar/editar condomínio via custom event ou storage).
  const condominios = condominiosDisponiveis || [];

  // Fecha dropdown de condominio ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (condoRef.current && !condoRef.current.contains(e.target)) setShowCondo(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const carregarNaoLidos = async () => {
    if (!idCondominio) return;
    try {
      const r = await comunicadosService.naoLidos({
        idCondominio,
        perfil: usuario?.perfil || "Administrador",
      });
      setComunicados(r.data || []);
    } catch {}
  };

  // Contagem de reservas pendentes (apenas para perfis administrativos)
  const podeVerReservas = ["Admin","Sindico","Gerente"].includes(usuario?.perfil);
  const carregarReservasPendentes = async () => {
    if (!idCondominio || !podeVerReservas) return;
    try {
      const r = await reservasService.pendentesCount(idCondominio);
      setReservasCount(r?.data?.total || 0);
    } catch {}
  };

  // WebSocket — comunicados + reservas em tempo real
  useSocket(idCondominio, {
    novo_comunicado: (payload) => setComunicados(prev => [payload, ...prev]),
    nova_reserva: (payload) => {
      if (!podeVerReservas) return;
      setReservasPend(prev => {
        if (prev.some(r => r.id === payload.id)) return prev;
        return [payload, ...prev].slice(0, 20);
      });
      setReservasCount(c => c + 1);
      // Beep + push nativo se a aba estiver em segundo plano
      tocarBeepNotificacao();
      try {
        if (typeof Notification !== "undefined"
            && Notification.permission === "granted"
            && (document.hidden || document.visibilityState === "hidden")) {
          new Notification(`Nova reserva — ${payload.nmArea || ""}`, {
            body: `${payload.nmCondomino || "Condômino"} • Un. ${payload.nmUnidade || "?"}${payload.bloco ? `/Bl.${payload.bloco}` : ""}`,
            tag: `reserva-${payload.id}`,
          });
        }
      } catch {}
    },
    reserva_atualizada: (payload) => {
      if (!podeVerReservas) return;
      // Se a reserva saiu do "Pendente", remove e atualiza contagem
      if (payload?.status && payload.status !== "Pendente") {
        setReservasPend(prev => prev.filter(r => r.id !== payload.id));
        carregarReservasPendentes(); // recontagem autoritativa
      }
    },
  });

  useEffect(() => {
    carregarNaoLidos();
    carregarReservasPendentes();
    const t = setInterval(() => {
      carregarNaoLidos();
      carregarReservasPendentes();
    }, 30000);
    return () => clearInterval(t);
  }, [idCondominio]);

  // Propaga contagem para o App (que repassa pro Sidebar)
  useEffect(() => {
    if (typeof onBadgesChange === "function") {
      onBadgesChange({ reservas: reservasCount });
    }
  }, [reservasCount]);

  // Fecha dropdowns ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setShowBell(false);
      if (userRef.current && !userRef.current.contains(e.target)) setShowUser(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleMarcarLido = async (id) => {
    try {
      await comunicadosService.marcarLido(id, {
        nmLeitor: usuario?.nome || "Administrador",
        perfil:   usuario?.perfil || "Administrador",
      });
      setComunicados(prev => prev.filter(c => c.id !== id));
    } catch {}
  };

  const handleMarcarTodos = async () => {
    try {
      await Promise.all(comunicados.map(c =>
        comunicadosService.marcarLido(c.id, {
          nmLeitor: usuario?.nome || "Administrador",
          perfil:   usuario?.perfil || "Administrador",
        })
      ));
      setComunicados([]);
      setShowBell(false);
    } catch {}
  };

  const qtdNaoLidos = comunicados.length;
  const qtdTotal = qtdNaoLidos + (podeVerReservas ? reservasCount : 0);
  const initials = usuario?.nome
    ? usuario.nome.split(" ").slice(0,2).map(n=>n[0]).join("").toUpperCase()
    : "AS";
  const perfilCor = PERFIL_COLOR[usuario?.perfil] || "#1B3A6B";

  const fmtDataReserva = (s) => {
    if (!s) return "";
    try {
      const dt = new Date(`${String(s).split("T")[0]}T12:00:00`);
      return dt.toLocaleDateString("pt-BR");
    } catch { return s; }
  };

  const handleIrReservas = () => {
    setShowBell(false);
    onNavigate?.("reservas");
  };

  const CATEGORIA_ICON = {
    Aviso:"bi-megaphone-fill", Assembleia:"bi-people-fill",
    Manutencao:"bi-tools", Financeiro:"bi-cash-coin",
    Seguranca:"bi-shield-fill", Obras:"bi-building-fill",
    Lazer:"bi-balloon-fill", Outro:"bi-bell-fill",
  };

  return (
    <header className={`topbar ${collapsed ? "sidebar-collapsed" : ""}`}>
      <button className="topbar-toggle" onClick={onToggle} title="Recolher menu">
        <i className="bi bi-list" />
      </button>

      {/* Breadcrumb */}
      <div className="topbar-breadcrumb">
        <span className="breadcrumb-item"><i className="bi bi-house" /></span>
        {breadcrumbs.map((bc, i) => (
          <React.Fragment key={i}>
            <span className="breadcrumb-sep"><i className="bi bi-chevron-right" /></span>
            <span className={`breadcrumb-item ${i===breadcrumbs.length-1?"active":""}`}>{bc}</span>
          </React.Fragment>
        ))}
      </div>

      {/* Condomínio ativo — sempre visível para Admin/Gerente */}
      {['Admin','Gerente','Sindico'].includes(usuario?.perfil) ? (
        <div ref={condoRef} style={{position:"relative", flexShrink:0, maxWidth:260}}>
          <div
            onClick={() => setShowCondo(p=>!p)}
            style={{
              display:"flex", alignItems:"center", gap:6,
              background: condominioAtivo ? "#EFF6FF" : "#FFF7ED",
              border: condominioAtivo ? "1px solid #BFDBFE" : "1px solid #FED7AA",
              borderRadius:8,
              padding:"5px 12px", fontSize:12.5,
              color: condominioAtivo ? "#1D4ED8" : "#C2410C",
              fontWeight:600,
              width:"100%", maxWidth:260, overflow:"hidden",
              cursor:"pointer", userSelect:"none",
              whiteSpace:"nowrap",
            }}>
            <i className={`bi ${condominioAtivo ? "bi-buildings" : "bi-building-exclamation"}`}
              style={{flexShrink:0}}/>
            <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>
              {condominioAtivo
                ? (condominioAtivo.nmFantasia || condominioAtivo.nmCondominio)
                : "Selecione um condomínio"}
            </span>
            <i className={`bi bi-chevron-${showCondo?"up":"down"}`} style={{fontSize:10, flexShrink:0}}/>
          </div>
          {/* Dropdown de troca de condomínio (estado vazio também) */}
          {showCondo && condominios.length === 0 && (
            <div className="topbar-panel" style={{
              position:"absolute", top:"calc(100% + 6px)", right:0,
              borderRadius:10, minWidth:260, maxWidth:320,
              zIndex:9999, padding:"14px 16px",
            }}>
              <div style={{fontSize:13,color:"var(--gray-700)",fontWeight:600,marginBottom:6}}>
                <i className="bi bi-buildings me-2"/>Nenhum condomínio disponível
              </div>
              <div style={{fontSize:12,color:"var(--gray-500)"}}>
                {usuario?.perfil === "Sindico"
                  ? "Você ainda não está vinculado a nenhum condomínio. Solicite ao administrador."
                  : "Você ainda não tem condomínios cadastrados. Acesse o menu Cadastros → Condomínios."}
              </div>
            </div>
          )}
          {showCondo && condominios.length > 0 && (
            <div className="topbar-panel" style={{
              position:"absolute", top:"calc(100% + 6px)", right:0,
              borderRadius:10, minWidth:260, maxWidth:320,
              zIndex:9999, overflow:"hidden",
            }}>
              <div style={{padding:"8px 12px 6px", fontSize:11, color:"var(--gray-400)",
                fontWeight:600, textTransform:"uppercase", letterSpacing:".5px",
                borderBottom:"1px solid var(--gray-100)"}}>
                Trocar condomínio
              </div>
              <div style={{maxHeight:280, overflowY:"auto"}}>
                {condominios.map(c => (
                  <div key={c.id}
                    onClick={() => {
                      onTrocarCondominio?.(c);
                      setShowCondo(false);
                    }}
                    className={`topbar-panel-item${condominioAtivo?.id===c.id ? " active-row" : ""}`}
                    style={{
                      padding:"10px 14px", cursor:"pointer", display:"flex",
                      alignItems:"center", gap:10,
                      background: condominioAtivo?.id===c.id ? "var(--bg-surface-alt)" : "transparent",
                      borderLeft: condominioAtivo?.id===c.id ? "3px solid var(--primary)" : "3px solid transparent",
                    }}>
                    <div style={{width:32,height:32,borderRadius:8,
                      background:condominioAtivo?.id===c.id?"var(--primary)":"var(--gray-100)",
                      display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <i className="bi bi-buildings" style={{
                        color:condominioAtivo?.id===c.id?"#fff":"var(--gray-400)", fontSize:14}}/>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--gray-800)",
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {c.nmFantasia || c.nmCondominio}
                      </div>
                      {c.cnpj && <div style={{fontSize:11,color:"var(--gray-400)"}}>{c.cnpj}</div>}
                    </div>
                    {condominioAtivo?.id===c.id && (
                      <i className="bi bi-check-circle-fill" style={{color:"var(--primary)",fontSize:14,flexShrink:0}}/>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Actions */}
      <div className="topbar-actions" style={{gap:6}}>

        <ThemeToggle />

        {/* Sino de Notificações (Comunicados + Reservas Pendentes) */}
        <div style={{position:"relative"}} ref={bellRef}>
          <button className="topbar-btn" title="Notificações"
            onClick={() => { setShowBell(p=>!p); setShowUser(false); }}
            style={{position:"relative"}}>
            <i className="bi bi-bell" />
            {qtdTotal > 0 && (
              <span style={{
                position:"absolute", top:4, right:4,
                background:"#EF4444", color:"#fff",
                borderRadius:"50%", width:17, height:17,
                fontSize:10, fontWeight:700,
                display:"flex", alignItems:"center", justifyContent:"center",
                border:"2px solid var(--primary)",
              }}>
                {qtdTotal > 9 ? "9+" : qtdTotal}
              </span>
            )}
          </button>

          {showBell && (
            <div className="topbar-panel" style={{
              position:"absolute", right:0, top:"calc(100% + 8px)",
              width:380, borderRadius:12, zIndex:2000, overflow:"hidden",
            }}>
              {/* ── Bloco: Reservas Pendentes ── */}
              {podeVerReservas && (
                <>
                  <div style={{padding:"12px 16px", borderBottom:"1px solid var(--gray-100)",
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    background:"var(--bg-warning-soft)"}}>
                    <span style={{fontWeight:700,fontSize:14,color:"#92400E"}}>
                      <i className="bi bi-calendar-check me-2"/>Reservas Pendentes
                      {reservasCount > 0 && (
                        <span style={{background:"#F59E0B",color:"#fff",borderRadius:10,
                          padding:"0 7px",fontSize:11,marginLeft:6}}>{reservasCount}</span>
                      )}
                    </span>
                    {reservasCount > 0 && (
                      <button style={{background:"none",border:"none",fontSize:12,
                        color:"#B45309",cursor:"pointer",fontWeight:600}}
                        onClick={handleIrReservas}>
                        Ver todas →
                      </button>
                    )}
                  </div>
                  <div style={{maxHeight:200, overflowY:"auto"}}>
                    {reservasPend.length === 0 && reservasCount === 0 ? (
                      <div style={{padding:"14px 16px",textAlign:"center",color:"var(--gray-400)",fontSize:12}}>
                        <i className="bi bi-check2-circle me-1"/>Sem reservas aguardando aprovação
                      </div>
                    ) : reservasPend.length === 0 ? (
                      <div style={{padding:"14px 16px",textAlign:"center",color:"var(--gray-500)",fontSize:12}}>
                        <span style={{color:"#92400E",fontWeight:600}}>{reservasCount}</span>{" "}
                        reserva(s) pendente(s). <button
                          style={{background:"none",border:"none",color:"var(--primary)",cursor:"pointer",padding:0,textDecoration:"underline"}}
                          onClick={handleIrReservas}>Abrir lista</button>
                      </div>
                    ) : reservasPend.map(r => (
                      <div key={r.id} onClick={handleIrReservas} style={{
                        padding:"10px 16px", borderBottom:"1px solid var(--gray-100)",
                        cursor:"pointer", display:"flex", gap:10, alignItems:"flex-start",
                      }}
                        className="topbar-panel-item"
                        onMouseEnter={e=>e.currentTarget.style.background="#FEF3C7"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div style={{width:32,height:32,borderRadius:8,background:"#FEF3C7",
                          display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          <i className="bi bi-calendar-check" style={{color:"#B45309",fontSize:15}} />
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:600,fontSize:12.5,color:"var(--gray-800)"}}>
                            {r.nmArea} — {fmtDataReserva(r.dtReserva)}
                          </div>
                          <div style={{fontSize:11.5,color:"var(--gray-500)"}}>
                            {r.nmCondomino || "Condômino"} • Un. {r.nmUnidade || "?"}{r.bloco ? `/Bl.${r.bloco}` : ""}
                            {r.hrInicio && ` • ${String(r.hrInicio).slice(0,5)}-${String(r.hrFim||"").slice(0,5)}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ── Bloco: Comunicados ── */}
              <div style={{padding:"12px 16px", borderBottom:"1px solid var(--gray-100)",
                display:"flex", justifyContent:"space-between", alignItems:"center",
                borderTop: podeVerReservas ? "1px solid var(--gray-200)" : "none"}}>
                <span style={{fontWeight:700,fontSize:14,color:"var(--gray-800)"}}>
                  <i className="bi bi-megaphone me-2"/>Comunicados
                  {qtdNaoLidos > 0 && (
                    <span style={{background:"#EF4444",color:"#fff",borderRadius:10,
                      padding:"0 7px",fontSize:11,marginLeft:6}}>{qtdNaoLidos}</span>
                  )}
                </span>
                {qtdNaoLidos > 0 && (
                  <button style={{background:"none",border:"none",fontSize:12,
                    color:"var(--primary)",cursor:"pointer",fontWeight:500}}
                    onClick={handleMarcarTodos}>
                    Marcar todos como lidos
                  </button>
                )}
              </div>
              <div style={{maxHeight:280, overflowY:"auto"}}>
                {comunicados.length === 0 ? (
                  <div style={{padding:"24px 16px",textAlign:"center",color:"var(--gray-400)"}}>
                    <i className="bi bi-bell-slash" style={{fontSize:24,display:"block",marginBottom:6}} />
                    <p style={{fontSize:12.5,margin:0}}>Nenhum comunicado não lido</p>
                  </div>
                ) : comunicados.map(c => (
                  <div key={c.id} style={{
                    padding:"12px 16px", borderBottom:"1px solid var(--gray-100)",
                    background:"var(--bg-surface-alt)", display:"flex", gap:12, alignItems:"flex-start",
                  }}>
                    <div style={{width:36,height:36,borderRadius:8,background:"var(--bg-highlight)",
                      display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <i className={`bi ${CATEGORIA_ICON[c.categoria]||"bi-bell-fill"}`}
                        style={{color:"var(--primary)",fontSize:17}} />
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,fontSize:13,color:"var(--gray-800)",marginBottom:2}}>
                        {c.titulo}
                      </div>
                      <div style={{fontSize:12,color:"var(--gray-500)"}}>
                        {c.categoria} • {c.prioridade}
                      </div>
                      <div style={{fontSize:11,color:"var(--gray-400)",marginTop:2}}>
                        {new Date(c.dtPublicacao).toLocaleString("pt-BR")}
                      </div>
                    </div>
                    <button
                      style={{background:"none",border:"none",color:"var(--gray-400)",
                        cursor:"pointer",fontSize:15,padding:"2px",flexShrink:0}}
                      title="Marcar como lido"
                      onClick={() => handleMarcarLido(c.id)}>
                      <i className="bi bi-check2" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal Meu Perfil (dados/senha/sessões) */}
        <MeuPerfilModal
          aberto={!!perfilModal}
          abaInicial={perfilModal || "dados"}
          usuario={usuario}
          onFechar={() => setPerfilModal(null)}
          onSenhaTrocada={() => {
            setPerfilModal(null);
            (onLogout || logout)?.();
          }}
        />

        {/* Avatar / Menu do usuário */}
        <div style={{position:"relative"}} ref={userRef}>
          <button onClick={() => { setShowUser(p=>!p); setShowBell(false); }}
            style={{
              width:36, height:36, borderRadius:"50%",
              background:`linear-gradient(135deg, ${perfilCor}, ${perfilCor}cc)`,
              border:"none", cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              color:"#fff", fontSize:13, fontWeight:700,
              boxShadow:"0 2px 8px rgba(0,0,0,.2)",
            }} title={usuario?.nome || "Minha conta"}>
            {initials}
          </button>

          {showUser && (
            <div className="topbar-panel" style={{
              position:"absolute", right:0, top:"calc(100% + 8px)",
              width:240, borderRadius:12, zIndex:2000, overflow:"hidden",
            }}>
              {/* Info do usuário */}
              <div style={{padding:"14px 16px",borderBottom:"1px solid var(--border-subtle)",
                background:"var(--bg-surface-alt)"}}>
                <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)"}}>
                  {usuario?.nome || "Usuário"}
                </div>
                <div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>
                  {usuario?.email || ""}
                </div>
                <span style={{
                  display:"inline-block",marginTop:6,
                  background:perfilCor+"15", color:perfilCor,
                  padding:"2px 10px", borderRadius:10,
                  fontSize:11, fontWeight:700,
                }}>
                  {PERFIL_LABEL[usuario?.perfil] || usuario?.perfil}
                </span>
              </div>

              {/* Ações */}
              <div style={{padding:"6px 0"}}>
                {[
                  {icon:"bi-person",      label:"Meu Perfil",
                   action:()=>{ setShowUser(false); setPerfilModal("dados"); }},
                  {icon:"bi-key",         label:"Alterar Senha",
                   action:()=>{ setShowUser(false); setPerfilModal("senha"); }},
                  {icon:"bi-shield-lock", label:"Sessões Ativas",
                   action:()=>{ setShowUser(false); setPerfilModal("sessoes"); }},
                  ...(usuario?.perfil === "Admin" && onAbrirOnboarding ? [
                    {icon:"bi-magic", label:"Assistente de Configuração",
                     action:()=>{ setShowUser(false); onAbrirOnboarding(); }},
                  ] : []),
                ].map(item => (
                  <button key={item.label} onClick={item.action}
                    style={{display:"flex",alignItems:"center",gap:10,width:"100%",
                      padding:"9px 16px",border:"none",background:"none",cursor:"pointer",
                      fontSize:13,color:"var(--gray-700)",textAlign:"left"}}
                    className="topbar-panel-item"
                    onMouseEnter={e=>e.currentTarget.style.background="var(--bg-hover)"}
                    onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    <i className={`bi ${item.icon}`} style={{fontSize:15,color:"var(--gray-400)"}}/>
                    {item.label}
                  </button>
                ))}
                <div style={{height:1,background:"var(--gray-100)",margin:"4px 0"}}/>
                <button onClick={() => { setShowUser(false); onLogout?.(); }}
                  style={{display:"flex",alignItems:"center",gap:10,width:"100%",
                    padding:"9px 16px",border:"none",background:"none",cursor:"pointer",
                    fontSize:13,color:"#991B1B",textAlign:"left"}}
                  onMouseEnter={e=>e.currentTarget.style.background="#FEF2F2"}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <i className="bi bi-box-arrow-right" style={{fontSize:15,color:"#991B1B"}}/>
                  Sair do Sistema
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
